import log from 'loglevel';
import { GraphQLOptionsV6 } from '@aws-amplify/api-graphql';
import { GenericRepository } from '@/repository/GenericRepository.ts';
import { Subscription } from 'rxjs';

export abstract class GenericService<T> {
  protected repository: GenericRepository<T>;

  constructor(repository: GenericRepository<T>) {
    this.repository = repository;
  }

  async get(id: string) {
    try {
      return await this.repository.get(id);
    } catch (error) {
      log.error('Error getting:', error);
      throw error;
    }
  }

  async list(listOptions?: GraphQLOptionsV6['variables']) {
    try {
      return await this.repository.list(listOptions);
    } catch (error) {
      log.error('Error listing:', error);
      throw error;
    }
  }

  async search(options: GraphQLOptionsV6['variables']) {
    try {
      return await this.repository.search(options);
    } catch (error) {
      log.error('Error searching:', error);
      throw error;
    }
  }

  async update(
    object: T | Omit<T, '__typename' | 'createdAt' | 'updatedAt' | 'owner'>
  ) {
    try {
      return await this.repository.update(object);
    } catch (error) {
      log.error('Error updating:', error);
      throw error;
    }
  }

  async save(
    object:
      T | Omit<T, '__typename' | 'id' | 'createdAt' | 'updatedAt' | 'owner'>
  ) {
    try {
      return await this.repository.save(object);
    } catch (error) {
      log.error('Error saving:', error);
      throw error;
    }
  }

  async delete(objectId: string) {
    try {
      return await this.repository.delete(objectId);
    } catch (error) {
      log.error('Error deleting:', error);
      throw error;
    }
  }

  onUpdateEvent<T>(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    execute: (data: T) => void
  ): Promise<Subscription | undefined> {
    log.debug('onUpdateEvent not implemented.');
    return new Promise(() => {});
  }

  onCreateEvent<T>(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    execute: (data: T) => void
  ): Promise<Subscription | undefined> {
    log.debug('onCreateEvent not implemented.');
    return new Promise(() => {});
  }

  onDeleteEvent<T>(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    execute: (data: T) => void
  ): Promise<Subscription | undefined> {
    log.debug('onDeleteEvent not implemented.');
    return new Promise(() => {});
  }

  protected async onEvent<T>(
    execute: (data: T) => void,
    graphQlSubscriptionString?: string
  ): Promise<Subscription | undefined> {
    if (!graphQlSubscriptionString) {
      return;
    }
    const subscription = (
      await this.repository.getGraphQLSubscription(graphQlSubscriptionString)
    ).subscribe({
      next: ({ data }: { data: unknown[] }) => {
        const object = Object.entries(data)[0][1] as T;
        execute(object);
      },
    });
    return subscription;
  }
}
